import { UseFormReturnType } from "@mantine/form"
import {
  CaretCircleUp,
  Circle,
  CurrencyInr,
  FileText,
  Paperclip,
  Percent,
  Plus,
  Trash,
} from "@phosphor-icons/react"
import {
  Button,
  FormInput,
  FormQuerySelector,
  Tabs,
  Tag,
  Toast,
} from "@vesatogo/grass-core"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import { cloneDeep } from "lodash-es"
import { useState } from "react"
import { toast } from "react-hot-toast"
import { Link } from "react-router-dom"
import AttachmentModal from "~/components/AttachmentModal"
import { InsufficientCreditNote } from "~/components/CreditManagement/InsufficientCreditNote"
import { NoCreditLimitNote } from "~/components/CreditManagement/NoCreditLimitNote"
import { UserCreditSummary } from "~/components/CreditManagement/UserCreditSummary"
import {
  GstBreakdownCard,
  type GstAttachment,
} from "~/components/GstBreakdownCard"
import DetailTable from "~/components/ProductionPlanning/DetailTable"
import { TradeKinds } from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { AppRoutes } from "~/constants/routes"
import {
  useAllCommoditiesQuery,
  useAllPlotCropsQuery,
  useAllSiUnitsQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useAuthenticatedUserQuery,
  useBuyerCreditWalletInfoSummaryQuery,
  useUsersQuery,
} from "~/generated/graphql"
import { ReceiptState } from "~/pages/d/trade-requests/receipts/types"
import {
  createEmptyReceiptCommodityBlock,
  getBuyerParticularsStats,
  getCommodityParticularsTotals,
  syncReceiptFormValues,
  type ReceiptCommodityBlock,
} from "~/utils/receiptParticularsItems"

type SharedEntity = { group: SharedEntityGroup }

type ReceiptParticularsProps = {
  form: UseFormReturnType<ReceiptState, (values: ReceiptState) => ReceiptState>
  inputProps: (name: string, sharedEntity?: SharedEntity) => any
  runComputations: (values: ReceiptState) => void
  isDisabled?: boolean
  defaultCurrency?: { name?: string }
  useCredit?: boolean
  allowMultipleCommodity?: boolean
  supplierSameState?: boolean
  sharedDeptId?: string
}

function ReceiptBuyerSection({
  buyerIndex,
  form,
  inputProps,
  runComputations,
  isDisabled,
  defaultCurrency,
  useCredit = true,
  allowMultipleCommodity = false,
  supplierSameState,
  sharedDeptId,
}: {
  buyerIndex: number
  form: ReceiptParticularsProps["form"]
  inputProps: ReceiptParticularsProps["inputProps"]
  runComputations: ReceiptParticularsProps["runComputations"]
  isDisabled?: boolean
  defaultCurrency?: ReceiptParticularsProps["defaultCurrency"]
  useCredit?: boolean
  allowMultipleCommodity?: boolean
  supplierSameState?: boolean
  sharedDeptId
}) {
  const [isOpen, setOpen] = useState(true)
  const [buyerAttachmentOpen, setBuyerAttachmentOpen] = useState(false)
  const [activeCommodityIndex, setActiveCommodityIndex] = useState(0)

  const [{ data }] = useAuthenticatedUserQuery()
  const me = data?.me
  const buyerGroups = ["Trader"]
  if (me?.permissions?.includes(Permission.SelectFarmerInBuyer)) {
    buyerGroups.push("Farmer")
  }

  const { values: state, setValues } = form
  const trade = state.trades[buyerIndex]
  const buyerSharedId = trade?.buyer?.shared_id

  const isCreditKind =
    state.kind === TradeKinds.Booking || state.kind === TradeKinds.HishobPatti

  const [{ data: creditWalletData, fetching }] =
    useBuyerCreditWalletInfoSummaryQuery({
      variables: { userId: buyerSharedId, deptId: sharedDeptId },
      pause: !buyerSharedId || !useCredit || !isCreditKind,
      requestPolicy: "network-only",
    })

  const creditInfo = creditWalletData?.buyer_credit_wallet_info
  const dynamicAvailable = creditInfo?.dynamic_available ?? 0
  const hasCreditConfigured = Boolean(creditInfo?.credit_wallet_id)

  const isValid = isCreditKind
    ? hasCreditConfigured &&
      dynamicAvailable > 0 &&
      dynamicAvailable >= (trade?.total_amount || 0)
    : true

  const noCreditUser = isCreditKind
    ? Boolean(trade?.buyer?.id) && !hasCreditConfigured
    : false

  const commodityBlocks: ReceiptCommodityBlock[] =
    trade?.commodity_items?.length && trade.commodity_items.length > 0
      ? trade.commodity_items
      : [{ grades: [{ grade: null, rate: 0, quantity: 0 }] }]

  const buyerStats = getBuyerParticularsStats(trade)

  const applyState = (stateCopy: ReceiptState) => {
    const synced = syncReceiptFormValues(stateCopy)
    setValues(synced)
    runComputations(synced)
  }

  const addCommodity = () => {
    const stateCopy = cloneDeep(state)
    const buyer = stateCopy.trades[buyerIndex]
    if (!buyer.commodity_items) buyer.commodity_items = []
    buyer.commodity_items.push(createEmptyReceiptCommodityBlock({}))
    setActiveCommodityIndex(buyer.commodity_items.length - 1)
    applyState(stateCopy)
  }

  const removeCommodity = (cIdx: number) => {
    const stateCopy = cloneDeep(state)
    const buyer = stateCopy.trades[buyerIndex]
    buyer.commodity_items?.splice(cIdx, 1)
    if (!buyer.commodity_items?.length) {
      buyer.commodity_items = [createEmptyReceiptCommodityBlock()]
    }
    applyState(stateCopy)
    setActiveCommodityIndex(prev => {
      const nextCount = stateCopy.trades[buyerIndex].commodity_items!.length
      if (prev > cIdx) return prev - 1
      return Math.min(prev, nextCount - 1)
    })
  }

  const addGrade = (cIdx: number) => {
    const stateCopy = cloneDeep(state)
    const block = stateCopy.trades[buyerIndex].commodity_items![cIdx]
    if (!block.grades) block.grades = []
    block.grades.unshift({
      grade: null,
      rate: 0,
      quantity: 0,
    })
    applyState(stateCopy)
  }

  const removeGrade = (cIdx: number, gIdx: number) => {
    const stateCopy = cloneDeep(state)
    const grades = stateCopy.trades[buyerIndex].commodity_items![cIdx].grades
    grades.splice(gIdx, 1)
    if (!grades.length) {
      stateCopy.trades[buyerIndex].commodity_items![cIdx].grades = [
        { grade: null, rate: 0, quantity: 0 },
      ]
    }
    applyState(stateCopy)
  }

  const removeBuyer = () => {
    const stateCopy = cloneDeep(state)
    stateCopy.trades.splice(buyerIndex, 1)
    applyState(stateCopy)
  }

  const copyTradeId = () => {
    if (!trade?.id) return
    navigator.clipboard.writeText(String(trade.id))
    toast.custom(<Toast title={"Trade Id copied!"} />)
  }

  const tabItems = commodityBlocks.map((block, cIdx) => ({
    id: `buyer-${buyerIndex}-commodity-${cIdx}`,
    header: block.commodity?.name || `Commodity ${cIdx + 1}`,
    panel: (
      <ReceiptCommodityPanel
        buyerIndex={buyerIndex}
        cIdx={cIdx}
        block={block}
        form={form}
        inputProps={inputProps}
        isDisabled={isDisabled}
        defaultCurrency={defaultCurrency}
        state={state}
        onAddGrade={() => addGrade(cIdx)}
        onRemoveGrade={gIdx => removeGrade(cIdx, gIdx)}
        onRemoveCommodity={
          allowMultipleCommodity && commodityBlocks.length > 1
            ? () => removeCommodity(cIdx)
            : undefined
        }
        isFirstBuyerFirstGradeUnit={buyerIndex === 0}
        supplierSameState={supplierSameState}
        runComputations={runComputations}
      />
    ),
  }))

  return (
    <div className="border rounded-lg mb-4 overflow-hidden">
      <div className="flex flex-wrap gap-3 w-full rounded-t-lg bg-yellow-100 p-3 text-sm font-medium text-gray-600">
        <div className="flex flex-wrap justify-between items-center gap-4 w-full">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <CaretCircleUp
              onClick={() => setOpen(prev => !prev)}
              className={classNames(
                "h-6 w-6 cursor-pointer hover:opacity-80 shrink-0 transition-transform",
                { "-rotate-90": !isOpen }
              )}
            />
            <span className="text-base shrink-0">Select Buyer</span>
            <FormQuerySelector
              isOptionDisabled={(option: any) =>
                state.trades.some(
                  (t, i) => i !== buyerIndex && t.buyer?.id === option?.id
                )
              }
              className="min-w-[14rem]"
              serverSideQuery
              dataHook={useUsersQuery}
              variables={{ groups: buyerGroups }}
              getOptionLabel={({ is_verified, name }) =>
                `${name} | ${
                  is_verified === false
                    ? "Rejected"
                    : is_verified === true
                    ? "Verified"
                    : "Pending Verification"
                }`
              }
              {...inputProps(`trades.${buyerIndex}.buyer`, {
                group: SharedEntityGroup.KiteUser,
              })}
            />
            <div className="flex flex-wrap gap-1 font-600 text-gray-600 items-center">
              <span>{buyerStats.items} items</span>
              <Circle weight="fill" className="!text-gray-400" size={5} />
              <span>
                {buyerStats.qty} {state.unit?.name || "kg"}
              </span>
              <Circle weight="fill" className="!text-gray-400" size={5} />
              <span className="text-green-700 font-semibold">
                {inr(buyerStats.cost)}
              </span>
            </div>
            {trade?.id ? (
              // <button
              //   type="button"
              //   className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              //   onClick={copyTradeId}
              // >
              //   Trade ID: #{trade.id}
              //   <Copy size={14} />
              // </button>
              <Tag
                leftIcon={<FileText />}
                intent="none"
                className="!bg-white"
                onClick={copyTradeId}
              >
                Trade ID: #{trade.id}
              </Tag>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              leftIcon={<Plus />}
              onClick={addCommodity}
              disabled={isDisabled || !isValid}
              variant="ghost"
              hidden={!allowMultipleCommodity}
            >
              Commodity
            </Button>
            <Button
              onClick={() => setBuyerAttachmentOpen(true)}
              variant="ghost"
              size="sm"
              intent="info"
              leftIcon={<Paperclip size={15} />}
            >
              {trade.attachments?.length || 0}
            </Button>
            <Button
              variant="ghost"
              leftIcon={<Trash />}
              onClick={removeBuyer}
              intent="danger"
              size="sm"
              disabled={isDisabled || state.trades.length <= 1}
            >
              Delete buyer
            </Button>
          </div>
        </div>
      </div>

      {isOpen ? (
        <div className="p-4 bg-white">
          {useCredit && (
            <>
              {noCreditUser && (
                <NoCreditLimitNote fetching={fetching}>
                  <div className="underline text-blue-300">
                    <Link
                      to={AppRoutes.creditDetail(
                        creditInfo?.latest_verified_credit_wallet_request_id ||
                          "new"
                      )}
                      target="_blank"
                    >
                      Click here
                    </Link>
                  </div>
                </NoCreditLimitNote>
              )}
              {Boolean(creditInfo?.credit_wallet_id) && (
                <UserCreditSummary credit={creditInfo} />
              )}
            </>
          )}

          <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-200 mb-4">
            {allowMultipleCommodity ? (
              <>
                <Tabs
                  className="flex-1 min-w-0"
                  tabs={tabItems}
                  // @ts-expect-error – Headless UI Tab.Group supports selectedIndex
                  selectedIndex={Math.min(
                    activeCommodityIndex,
                    commodityBlocks.length - 1
                  )}
                  onChange={(_tab, index) =>
                    setActiveCommodityIndex(index ?? 0)
                  }
                />
              </>
            ) : (
              <ReceiptCommodityPanel
                buyerIndex={buyerIndex}
                cIdx={0}
                block={commodityBlocks[0]}
                form={form}
                inputProps={inputProps}
                isDisabled={isDisabled}
                defaultCurrency={defaultCurrency}
                state={state}
                onAddGrade={() => addGrade(0)}
                onRemoveGrade={gIdx => removeGrade(0, gIdx)}
                isFirstBuyerFirstGradeUnit={buyerIndex === 0}
                supplierSameState={supplierSameState}
                runComputations={runComputations}
              />
            )}
          </div>

          {useCredit && noCreditUser ? null : trade?.buyer?.id && !isValid ? (
            <InsufficientCreditNote>
              <div className="underline text-blue-300">
                <Link
                  to={AppRoutes.creditDetail(
                    creditInfo?.latest_verified_credit_wallet_request_id ||
                      "new"
                  )}
                  target="_blank"
                >
                  Click here
                </Link>
              </div>
            </InsufficientCreditNote>
          ) : null}
        </div>
      ) : null}

      {buyerAttachmentOpen ? (
        <AttachmentModal
          form={form}
          disabled={isDisabled}
          isOpen={buyerAttachmentOpen}
          attachments={trade.attachments || []}
          addAttachment={attachments => {
            form.setFieldValue(`trades.${buyerIndex}.attachments`, attachments)
          }}
          onClose={() => setBuyerAttachmentOpen(false)}
        />
      ) : null}
    </div>
  )
}

function ReceiptCommodityPanel({
  buyerIndex,
  cIdx,
  block,
  form,
  inputProps,
  isDisabled,
  defaultCurrency,
  state,
  onAddGrade,
  onRemoveGrade,
  onRemoveCommodity,
  isFirstBuyerFirstGradeUnit,
  supplierSameState,
  runComputations,
}: {
  buyerIndex: number
  cIdx: number
  block: ReceiptCommodityBlock
  form: ReceiptParticularsProps["form"]
  inputProps: ReceiptParticularsProps["inputProps"]
  isDisabled?: boolean
  defaultCurrency?: ReceiptParticularsProps["defaultCurrency"]
  state: ReceiptState
  onAddGrade: () => void
  onRemoveGrade: (gIdx: number) => void
  onRemoveCommodity?: () => void
  isFirstBuyerFirstGradeUnit: boolean
  supplierSameState?: boolean
  runComputations: ReceiptParticularsProps["runComputations"]
}) {
  const grades = block.grades?.length
    ? block.grades
    : [{ grade: null, rate: 0, quantity: 0 }]
  const commodityTotals = getCommodityParticularsTotals(block)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormQuerySelector
          label="Commodity"
          placeholder="Select"
          required
          serverSideQuery
          dataHook={useAllCommoditiesQuery}
          {...inputProps(
            `trades.${buyerIndex}.commodity_items.${cIdx}.commodity`,
            { group: SharedEntityGroup.KiteCommodity }
          )}
          disabled={isDisabled}
        />
        <FormQuerySelector
          label="Variety"
          placeholder="Select"
          required
          dataHook={useAllVarietiesQuery}
          variables={{
            commodity_id:
              state.trades[buyerIndex]?.commodity_items?.[cIdx]?.commodity?.id,
          }}
          {...inputProps(
            `trades.${buyerIndex}.commodity_items.${cIdx}.commodity_variety`,
            { group: SharedEntityGroup.KiteVariety }
          )}
          disabled={isDisabled}
        />

        <div className="flex gap-1">
          <FormQuerySelector
            label="Plot Selection"
            placeholder="Select"
            dataHook={useAllPlotCropsQuery}
            variables={{
              farmer: state?.seller?.id,
              crop: state.trades[buyerIndex]?.commodity_items?.[cIdx]?.commodity
                ?.crop_id,
            }}
            getOptionLabel={({ tag, name, external_reference_id, id }: any) => {
              if (!id) return undefined
              return `${tag} ${name} | ${external_reference_id}`
            }}
            {...inputProps(
              `trades.${buyerIndex}.commodity_items.${cIdx}.plot_crop`,
              { group: SharedEntityGroup.KitePlotCrop }
            )}
            disabled={isDisabled || !state?.seller?.id}
            className={"!w-full"}
          />
          {onRemoveCommodity ? (
            <div className="flex justify-end">
              <Button
                variant="minimal"
                intent="danger"
                size="sm"
                leftIcon={<Trash className="!mr-0" />}
                onClick={onRemoveCommodity}
                className="!self-end mb-1"
              />
            </div>
          ) : null}
        </div>
      </div>

      <DetailTable
        className="grid-cols-4"
        headers={[
          {
            label: "Grade",
            field: "grade",
            required: true,
            render: (gIdx: number) => (
              <FormQuerySelector
                placeholder="Select"
                dataHook={useAllVarietyGradesQuery}
                variables={{
                  variety:
                    state.trades[buyerIndex]?.commodity_items?.[cIdx]
                      ?.commodity_variety?.id,
                }}
                {...inputProps(
                  `trades.${buyerIndex}.commodity_items.${cIdx}.grades.${gIdx}.grade`,
                  { group: SharedEntityGroup.KiteGrade }
                )}
                isDisabled={isDisabled}
                isOptionDisabled={(opt: any) =>
                  grades.some((g, i) => i !== gIdx && g.grade?.id === opt.id)
                }
                className={"w-full"}
              />
            ),
          },
          {
            label: "Rate",
            field: "rate",
            required: true,
            hideInTotal: true,
            render: (gIdx: number) => (
              <FormInput
                placeholder="Enter"
                type="number"
                {...inputProps(
                  `trades.${buyerIndex}.commodity_items.${cIdx}.grades.${gIdx}.rate`
                )}
                disabled={isDisabled}
                rightElement={
                  <Tag className="mr-2" variant="minimal">
                    /{block.quantity_unit?.name || state.unit?.name || "kg"}
                  </Tag>
                }
              />
            ),
          },
          {
            label: "Quantity",
            field: "quantity",
            required: true,
            showTotal: true,
            render: (gIdx: number) => (
              <FormInput
                placeholder="Enter"
                type="number"
                className="vt-weight-listner"
                inputProps={{
                  id:
                    isFirstBuyerFirstGradeUnit && cIdx === 0 && gIdx === 0
                      ? "vt_weight_listener"
                      : undefined,
                }}
                {...inputProps(
                  `trades.${buyerIndex}.commodity_items.${cIdx}.grades.${gIdx}.quantity`
                )}
                disabled={isDisabled}
                rightElement={
                  <FormQuerySelector
                    components={{
                      IndicatorSeparator: () => null,
                      ClearIndicator: () => null,
                    }}
                    getControlStyles={(prev: any) => ({
                      ...prev,
                      border: "none",
                    })}
                    dataHook={useAllSiUnitsQuery}
                    variables={{ type: ["Weight", "SKU"] }}
                    {...inputProps(
                      `trades.${buyerIndex}.commodity_items.${cIdx}.quantity_unit`,
                      {
                        group: SharedEntityGroup.KiteSiUnit,
                      }
                    )}
                    disabled={isDisabled ? true : gIdx !== 0}
                  />
                }
              />
            ),
          },
          {
            label: "Total Cost",
            field: "total_cost",
            showTotal: true,
            render: (gIdx: number) => {
              const grade = grades[gIdx]
              return (
                <FormInput
                  placeholder="0"
                  leftElement={<CurrencyInr />}
                  value={inr(
                    (Number(grade.quantity) || 0) * (Number(grade.rate) || 0)
                  )}
                  disabled
                  className="!w-full"
                />
              )
            },
          },
        ]}
        rows={grades.map(g => ({
          ...g,
          total_cost: (Number(g.quantity) || 0) * (Number(g.rate) || 0),
        }))}
        addRowLabel="Grade"
        onAddRow={onAddGrade}
        onRemoveRow={onRemoveGrade}
        namePrefix={`trades.${buyerIndex}.commodity_items.${cIdx}.grades`}
        inputProps={inputProps}
        addDisable={isDisabled}
        deleteDisable={isDisabled}
        showTotals
        totals={{
          quantity: commodityTotals.quantity,
          total_cost: commodityTotals.total_cost,
        }}
      />

      {(() => {
        const totalSupplierGst =
          (block.supplier_igst || 0) +
          (block.supplier_sgst || 0) +
          (block.supplier_cgst || 0)
        const setGstField = (fields: Record<string, any>) => {
          const updatedValues = cloneDeep(form.values)
          const blk =
            updatedValues.trades?.[buyerIndex]?.commodity_items?.[cIdx]
          Object.entries(fields).forEach(([field, value]) => {
            form.setFieldValue(
              `trades.${buyerIndex}.commodity_items.${cIdx}.${field}`,
              value
            )
            if (blk) {
              ;(blk as any)[field] = value
            }
          })
          runComputations(updatedValues)
        }
        return (
          <GstBreakdownCard
            label="Supplier"
            autoCalculated={block.supplier_gst_auto_calculated ?? true}
            onAutoChange={val => {
              setGstField({
                supplier_gst_auto_calculated: val,
                _gstFromBackend: false,
              })
            }}
            isDisabled={isDisabled}
            isSameState={supplierSameState}
            totalGst={totalSupplierGst}
            igst={block.supplier_igst || 0}
            sgst={block.supplier_sgst || 0}
            cgst={block.supplier_cgst || 0}
            igstPct={block.supplier_igst_percentage}
            sgstPct={block.supplier_sgst_percentage}
            cgstPct={block.supplier_cgst_percentage}
            igstField="supplier_igst_percentage"
            sgstField="supplier_sgst_percentage"
            cgstField="supplier_cgst_percentage"
            onPctChange={(field, pct) => {
              setGstField({ [field]: pct })
            }}
            attachments={block.seller_gst_attachments || []}
            onAddAttachment={(newOnes: GstAttachment[]) => {
              setGstField({
                seller_gst_attachments: [
                  ...(block.seller_gst_attachments || []),
                  ...newOnes,
                ],
              })
            }}
            onRemoveAttachment={(index: number) => {
              const arr = [...(block.seller_gst_attachments || [])]
              arr.splice(index, 1)
              setGstField({ seller_gst_attachments: arr })
            }}
          />
        )
      })()}

      <div className="grid grid-cols-4 gap-4 text-base text-gray-800 p-3 bg-gray-50 rounded border border-dashed border-gray-200">
        <div className="col-span-2 font-medium">
          Total:{" "}
          <span className=" text-gray-600 ">
            {`${block?.commodity_variety?.name || ""}`}{" "}
          </span>
          <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
        </div>
        <div>
          {commodityTotals.quantity}{" "}
          {block.quantity_unit?.name || state.unit?.name || "kg"}
        </div>
        <div className="text-right font-medium">
          {inr(commodityTotals.total_cost)}
        </div>
      </div>
    </div>
  )
}

export const ReceiptParticulars = ({
  form,
  inputProps,
  runComputations,
  isDisabled,
  defaultCurrency,
  useCredit,
  allowMultipleCommodity,
  supplierSameState,
  sharedDeptId,
}: ReceiptParticularsProps) => {
  const { values: state } = form

  const getKg = (quantity: number, unit?: any) =>
    (Number(quantity) || 0) *
    (Number(unit?.base_conversion ?? unit?.si_unit?.[0]?.base_conversion) || 0)

  const buyerBreakdown = state.trades.map((trade, buyerIndex) => {
    const commodityRows = trade.commodity_items?.length
      ? trade.commodity_items.map((block, cIdx) => {
          const totals = getCommodityParticularsTotals(block)
          return {
            name: block.commodity?.name || `Commodity ${cIdx + 1}`,
            quantity: totals.quantity,
            unit: block.quantity_unit?.name || state.unit?.name || "kg",
            totalKg: getKg(totals.quantity, block.quantity_unit),
            total: totals.total_cost,
          }
        })
      : trade.items?.length
      ? trade.items.map((item, iIdx) => {
          const quantity = Number(item.quantity) || 0
          return {
            name: item.grade?.name || `Item ${iIdx + 1}`,
            quantity,
            unit: state.unit?.name || "kg",
            totalKg: getKg(quantity, (item as any).quantity_unit ?? state.unit),
            total: Number(item.total_amount) || 0,
          }
        })
      : []

    const buyerTotal = commodityRows.reduce((acc, row) => acc + row.total, 0)
    const buyerKg = commodityRows.reduce((acc, row) => acc + row.totalKg, 0)
    return {
      buyer: trade.buyer,
      commodityRows,
      buyerTotal,
      buyerKg,
    }
  })

  const allBuyersTotal = buyerBreakdown.reduce(
    (acc, buyer) => acc + buyer.buyerTotal,
    0
  )
  const allBuyersKg = buyerBreakdown.reduce(
    (acc, buyer) => acc + buyer.buyerKg,
    0
  )
  const finalTotal = allBuyersTotal - (state.amount_deducted || 0)

  return (
    <div>
      <div className="p-4">
        {state.trades?.map((buyer, index) => (
          <ReceiptBuyerSection
            key={buyer.id ?? `buyer-${index}`}
            buyerIndex={index}
            form={form}
            inputProps={inputProps}
            runComputations={runComputations}
            isDisabled={isDisabled}
            defaultCurrency={defaultCurrency}
            useCredit={useCredit}
            allowMultipleCommodity={allowMultipleCommodity}
            supplierSameState={supplierSameState}
            sharedDeptId={sharedDeptId}
          />
        ))}
      </div>

      <hr className="border-gray-300" />
      <div className="grid grid-cols-4 gap-4 text-base p-4">
        <div className="font-medium col-span-2">
          Total Consolidated Commodities Amount{" "}
          <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
        </div>
        <div>
          {state.total_quantity} {"Units"}
          {allBuyersKg > 0 ? ` (${allBuyersKg} kg)` : ""}
        </div>
        <div className="flex gap-2 justify-end">
          <span>{inr(allBuyersTotal)}</span>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 text-base p-4">
        <div className="col-span-2">
          <FormInput
            inline
            labelProps={{ className: "text-base" }}
            label="Standard Deduction of Wt"
            size="sm"
            type="number"
            rightElement={<Percent />}
            {...inputProps("standard_deduction")}
            min={0}
            max={100}
          />
        </div>
        <div className="text-red-500">
          {state.quantity_deducted?.toFixed(2)} {state.unit?.name || "na"}
        </div>
        <div className="flex justify-between">
          <span />
          <span className="text-red-500">
            {inr(state.amount_deducted || 0)}
          </span>
        </div>
      </div>
      <footer className="grid grid-cols-4 gap-4 text-base p-4 bg-green-100 rounded-b-lg">
        <div className="font-500">
          Final Total{" "}
          <span className="italic text-gray-500 text-sm"> (Excl. Tax)</span>
        </div>
        <div />
        <div>
          {(state.total_quantity || 0) - (state.quantity_deducted || 0)}{" "}
          {"Units"}
        </div>
        <div className="text-right font-bold text-lg">
          <span>{inr(finalTotal)}</span>
        </div>
      </footer>
    </div>
  )
}

export type { ReceiptParticularsProps }
